import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/base/SEOHead';

export default function SobrePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('historia');

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "TravelAgency",
      "name": "FF TRAVEL",
      "description":
        "Agência de viagens líder em Angola, especializada em viagens internacionais, venda de telemóveis e compra/venda de ouro em Luanda",
      "foundingDate": "2015",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Rua Rainha Ginga, Edifício Atlântico, 3º Andar",
        "addressLocality": "Luanda",
        "addressCountry": "AO",
      },
      "telephone": ["+244936769113", "+244927606910"],
      "email": "fftravel437@gmail.com",
      "url": "https://fftravel.ao",
      "sameAs": [
        "https://facebook.com/fftravelangola",
        "https://instagram.com/fftravelangola",
      ],
      "areaServed": {
        "@type": "Country",
        "name": "Angola",
      },
      "knowsAbout": [
        "Viagens Internacionais",
        "Venda de Telemóveis",
        "Compra e Venda de Ouro",
        "Turismo",
        "Tecnologia",
      ],
      "slogan": "Conectando Angola ao Mundo",
    },
  };

  /* --------------------------------------------------------------
   * Scroll handling – adds a background to the navigation bar when
   * the page is scrolled more than 50 px. The listener is safely
   * removed on unmount to avoid memory‑leaks.
   * -------------------------------------------------------------- */
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <SEOHead
        title="Sobre Nós - FF TRAVEL | História e Missão da Nossa Agência em Luanda Angola"
        description="Conheça a FF TRAVEL, agência de viagens líder em Angola desde 2015. Especialistas em viagens internacionais, venda de telemóveis iPhone e compra/venda de ouro em Luanda. Equipa profissional dedicada a conectar Angola ao mundo."
        canonical="https://fftravel.ao/sobre"
        keywords="sobre FF Travel, agência viagens Angola história, equipa FF Travel Luanda, missão visão valores, empresa angolana turismo, FF Travel quem somos"
        ogImage="https://public.readdy.ai/ai/img_res/6ebb5605615a998e388f6d5a57c96451.jpg"
        schemaData={schemaData}
      />

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/06b8625833a048238e39980ff5e819c9.png"
            alt="Equipa profissional FF Travel Angola - agência de viagens líder em Luanda"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Sobre a FF TRAVEL</h1>
          <p className="text-xl md:text-2xl font-light">
            Conectando Angola ao Mundo desde 2015
          </p>
        </div>
      </section>

      {/* Navegação */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/821bfe14b1fc609bf2a57dd541a10a42.png"
                alt="FF TRAVEL - Agência de Viagens"
                className="h-24 md:h-28"
              />
            </Link>

            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className="text-[#1e40af] hover:text-[#ff6b35] transition-colors font-medium"
              >
                Início
              </Link>
              <Link
                to="/agencia-viagens"
                className="text-[#1e40af] hover:text-[#ff6b35] transition-colors font-medium"
              >
                Agência de Viagens
              </Link>
              <Link
                to="/loja-servicos"
                className="text-[#1e40af] hover:text-[#ff6b35] transition-colors font-medium"
              >
                Loja & Serviços
              </Link>
              <Link to="/sobre" className="text-[#ff6b35] font-semibold">
                Sobre
              </Link>
              <Link
                to="/contactos"
                className="text-[#1e40af] hover:text-[#ff6b35] transition-colors font-medium"
              >
                Contactos
              </Link>
            </div>

            <a
              href="https://wa.me/244936769113"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:block bg-[#ff6b35] text-white px-6 py-3 rounded-full font-medium hover:bg-[#ff5722] transition-all shadow-md whitespace-nowrap cursor-pointer"
            >
              Pedir Cotação
            </a>
          </div>
        </div>
      </nav>

      {/* Nossa História */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                A Nossa História
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Fundada em 2015, a <strong>FF TRAVEL</strong> nasceu com a
                  missão de revolucionar o setor de viagens em Angola,
                  oferecendo serviços de excelência e conectando os angolanos aos
                  principais destinos do mundo.
                </p>
                <p>
                  Ao longo dos anos, expandimos os nossos serviços para incluir
                  a <strong>venda de telemóveis de última geração</strong> e
                  a <strong>compra e venda de ouro</strong>, tornando‑nos uma
                  referência multisserviços em Luanda.
                </p>
                <p>
                  Hoje, somos reconhecidos pela nossa <strong>credibilidade</strong>,
                  <strong>profissionalismo</strong> e <strong>compromisso com a
                  satisfação dos nossos clientes</strong>, servindo milhares de
                  angolanos todos os anos.
                </p>
              </div>
            </div>

            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/baad2e9e67762e77450cb6974d687209.jpeg"
                alt="Escritório FF Travel Luanda - área de receção moderna e acolhedora para atendimento ao cliente"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Missão, Visão e Valores */}
      <section className="py-20 px-4 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Missão, Visão e Valores
            </h2>
            <p className="text-xl text-gray-600">
              Os pilares que guiam o nosso trabalho
            </p>
          </div>

          {/* Tabs */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-white rounded-full p-1 shadow-lg">
              <button
                onClick={() => setActiveTab('historia')}
                className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'historia'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Missão
              </button>
              <button
                onClick={() => setActiveTab('visao')}
                className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'visao'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Visão
              </button>
              <button
                onClick={() => setActiveTab('valores')}
                className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'valores'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Valores
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-4xl mx-auto">
            {activeTab === 'historia' && (
              <div className="bg-white rounded-3xl p-12 shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-compass-3-line text-3xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-6 text-gray-900">
                      Nossa Missão
                    </h3>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Proporcionar experiências de viagem inesquecíveis e
                      serviços de excelência em tecnologia e ouro, conectando os
                      angolanos ao mundo com <strong>profissionalismo</strong>,
                      <strong>segurança</strong> e <strong>atendimento
                      personalizado</strong>. Comprometemo‑nos a superar as
                      expectativas dos nossos clientes em cada interação.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'visao' && (
              <div className="bg-white rounded-3xl p-12 shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-eye-line text-3xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-6 text-gray-900">
                      Nossa Visão
                    </h3>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Ser a <strong>agência de referência em Angola</strong> nos
                      setores de viagens, tecnologia e comércio de ouro,
                      reconhecida pela <strong>inovação</strong>,{' '}
                      <strong>confiança</strong> e{' '}
                      <strong>excelência no atendimento</strong>. Aspiramos
                      expandir a nossa presença nacional e internacional,
                      tornando‑nos a primeira escolha dos angolanos.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'valores' && (
              <div className="bg-white rounded-3xl p-12 shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-line text-3xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-6 text-gray-900">
                      Nossos Valores
                    </h3>
                    <ul className="space-y-4 text-lg text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-2xl text-amber-500 flex-shrink-0 mt-1"></i>
                        <span>
                          <strong>Integridade:</strong> Transparência e
                          honestidade em todas as nossas relações
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-2xl text-amber-500 flex-shrink-0 mt-1"></i>
                        <span>
                          <strong>Excelência:</strong> Compromisso com a
                          qualidade superior em todos os serviços
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-2xl text-amber-500 flex-shrink-0 mt-1"></i>
                        <span>
                          <strong>Inovação:</strong> Busca constante por
                          soluções criativas e tecnológicas
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-2xl text-amber-500 flex-shrink-0 mt-1"></i>
                        <span>
                          <strong>Respeito:</strong> Valorização das pessoas e
                          da diversidade cultural
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-2xl text-amber-500 flex-shrink-0 mt-1"></i>
                        <span>
                          <strong>Compromisso:</strong> Dedicação total à
                          satisfação dos nossos clientes
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* VÍDEO DE COMEMORAÇÃO - VERSÃO MELHORADA */}
      <section className="py-24 bg-gradient-to-br from-[#1e40af] to-[#1e3a8a] relative overflow-hidden">
        {/* Elementos decorativos de fundo */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#ff6b35] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block bg-[#ff6b35] text-white px-6 py-3 rounded-full text-sm font-bold mb-6 uppercase tracking-wide animate-pulse">
              🎉 Celebrando Nossa Trajetória
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              FF Travel - Nossa História em Movimento
            </h2>
            <p className="text-white/90 text-xl max-w-3xl mx-auto leading-relaxed">
              Assista ao vídeo especial de comemoração do estabelecimento da FF Travel. Uma jornada de sucesso, profissionalismo e realização de sonhos!
            </p>
          </div>

          {/* Container do Vídeo com Destaque Especial */}
          <div className="relative group">
            {/* Moldura Brilhante Animada */}
            <div className="absolute -inset-4 bg-gradient-to-r from-[#ff6b35] via-yellow-400 to-[#ff6b35] rounded-3xl opacity-75 blur-xl group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>

            {/* Card do Vídeo */}
            <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-4 shadow-2xl border-4 border-white/20">
              {/* Badge de Destaque */}
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-20">
                <div className="bg-[#ff6b35] text-white px-8 py-3 rounded-full font-bold text-lg shadow-2xl flex items-center gap-3">
                  <i className="ri-play-circle-fill text-3xl animate-bounce"></i>
                  <span>Clique para Assistir</span>
                </div>
              </div>

              {/* Vídeo do Instagram */}
              <div className="relative aspect-[9/16] max-w-md mx-auto rounded-2xl overflow-hidden shadow-2xl">
                <iframe
                  src="https://www.instagram.com/reel/DQ7LssjjifA/embed"
                  className="w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  allowFullScreen
                  title="FF Travel - Comemoração do Estabelecimento"
                ></iframe>

                {/* Overlay com Ícone de Play (aparece antes do clique) */}
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="w-24 h-24 bg-[#ff6b35] rounded-full flex items-center justify-center shadow-2xl animate-pulse">
                    <i className="ri-play-fill text-5xl text-white"></i>
                  </div>
                </div>
              </div>

              {/* Informações Adicionais */}
              <div className="mt-6 text-center">
                <div className="flex items-center justify-center gap-6 text-white/90">
                  <div className="flex items-center gap-2">
                    <i className="ri-trophy-line text-2xl text-yellow-400"></i>
                    <span className="font-semibold">Estabelecimento</span>
                  </div>
                  <div className="w-1 h-6 bg-white/30"></div>
                  <div className="flex items-center gap-2">
                    <i className="ri-star-fill text-2xl text-yellow-400"></i>
                    <span className="font-semibold">Crescimento</span>
                  </div>
                  <div className="w-1 h-6 bg-white/30"></div>
                  <div className="flex items-center gap-2">
                    <i className="ri-heart-fill text-2xl text-red-400"></i>
                    <span className="font-semibold">Sucesso</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Texto Motivacional */}
          <div className="mt-12 text-center">
            <p className="text-white/80 text-lg mb-6 max-w-2xl mx-auto">
              Desde o nosso estabelecimento, temos ajudado centenas de clientes a realizarem seus sonhos de viajar pelo mundo. Esta é apenas o começo da nossa história de sucesso!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/244936769113"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#ff6b35] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-[#ff5722] transition-all shadow-2xl whitespace-nowrap cursor-pointer"
              >
                Fazer Parte da Nossa História
              </a>
              <Link
                to="/agencia-viagens"
                className="inline-block bg-white text-[#1e40af] px-10 py-5 rounded-full font-bold text-lg hover:bg-gray-100 transition-all shadow-2xl whitespace-nowrap cursor-pointer"
              >
                Conhecer Nossos Serviços
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CEO da FF Travel - Imagem em Tela Cheia */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block bg-[#1e40af]/10 text-[#1e40af] px-6 py-3 rounded-full text-sm font-bold mb-6 uppercase tracking-wider">
              CEO da FF Travel
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a8a] mb-4">
              Profissionalismo em Ação
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Conheça o CEO da FF Travel em momentos importantes de networking e parcerias estratégicas que fortalecem nosso compromisso com a excelência
            </p>
          </div>

          <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
            {/* Imagem Principal - SEM RUÍDO */}
            <div className="relative aspect-[16/10] bg-gradient-to-br from-gray-100 to-gray-50">
              <img
                src="https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/19da29166cca9efd4d00bd29171c8b53.jpeg"
                alt="CEO da FF Travel"
                className="w-full h-full object-cover object-center"
                style={{
                  imageRendering: '-webkit-optimize-contrast',
                  filter: 'contrast(1.05) brightness(1.02) saturate(1.1)',
                }}
              />

              {/* Overlay Suave */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>

            {/* Informações Sobrepostas */}
            <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm p-8">
              <div className="max-w-5xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#1e40af] rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-handshake-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Parcerias</p>
                      <p className="text-[#1e3a8a] font-bold">Estratégicas</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#1e40af] rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-team-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Networking</p>
                      <p className="text-[#1e3a8a] font-bold">Internacional</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#ff6b35] rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-award-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Credibilidade</p>
                      <p className="text-[#1e3a8a] font-bold">Comprovada</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Texto Adicional */}
          <div className="mt-12 text-center max-w-3xl mx-auto">
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              A FF Travel mantém parcerias estratégicas com empresas e instituições de renome internacional, garantindo os melhores serviços e condições para nossos clientes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/244936769113"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#ff6b35] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#ff5722] transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                Falar com Nossa Equipa
              </a>
              <Link
                to="/contactos"
                className="inline-block bg-[#1e40af] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#1e3a8a] transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                Ver Todos os Contactos
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NOVA SEÇÃO - Liderança e Networking */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-block bg-[#ff6b35]/10 text-[#ff6b35] px-6 py-3 rounded-full text-sm font-bold mb-6 uppercase tracking-wider">
              Liderança Estratégica
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a8a] mb-4">
              Construindo Parcerias Globais
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Nossa liderança está constantemente em eventos e reuniões estratégicas, fortalecendo parcerias que beneficiam diretamente nossos clientes
            </p>
          </div>

          <div className="relative rounded-3xl overflow-hidden shadow-2xl group animate-slide-up">
            {/* Imagem Principal */}
            <div className="relative aspect-[16/9] bg-gradient-to-br from-gray-100 to-gray-50">
              <img
                src="https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/ec646304adf8ddc925c6467ad8ff60ac.jpeg"
                alt="Liderança FF Travel em Networking Estratégico"
                className="w-full h-full object-cover object-center"
                style={{
                  imageRendering: '-webkit-optimize-contrast',
                  filter: 'contrast(1.05) brightness(1.02) saturate(1.1)',
                }}
              />

              {/* Overlay Gradiente */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

              {/* Badges Flutuantes */}
              <div className="absolute top-6 left-6 flex flex-wrap gap-3">
                <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2 shadow-lg animate-bounce">
                  <i className="ri-global-line text-xl text-[#1e40af]"></i>
                  <span className="text-sm font-bold text-[#1e3a8a]">Networking Global</span>
                </div>
                <div
                  className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2 shadow-lg animate-bounce"
                  style={{ animationDelay: '0.2s' }}
                >
                  <i className="ri-briefcase-line text-xl text-[#ff6b35]"></i>
                  <span className="text-sm font-bold text-[#1e3a8a]">Parcerias Estratégicas</span>
                </div>
              </div>
            </div>

            {/* Informações Sobrepostas - Redesenhadas */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white to-white/95 backdrop-blur-md p-8">
              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="flex items-center gap-4 group/item hover:scale-105 transition-transform">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#1e40af] to-[#1e3a8a] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <i className="ri-building-2-line text-3xl text-white"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Eventos</p>
                      <p className="text-[#1e3a8a] font-bold text-lg">Corporativos</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 group/item hover:scale-105 transition-transform">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#1e40af] to-[#1e3a8a] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <i className="ri-shake-hands-line text-3xl text-white"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Reuniões</p>
                      <p className="text-[#1e3a8a] font-bold text-lg">Estratégicas</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 group/item hover:scale-105 transition-transform">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#ff6b35] to-[#ff5722] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <i className="ri-trophy-line text-3xl text-white"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Excelência</p>
                      <p className="text-[#1e3a8a] font-bold text-lg">Reconhecida</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 group/item hover:scale-105 transition-transform">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#ff6b35] to-[#ff5722] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <i className="ri-team-line text-3xl text-white"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Conexões</p>
                      <p className="text-[#1e3a8a] font-bold text-lg">Internacionais</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Texto Explicativo */}
          <div className="mt-12 bg-white rounded-2xl p-10 shadow-lg animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <div className="max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6 text-center">
                Compromisso com a Excelência
              </h3>
              <p className="text-gray-700 text-lg leading-relaxed mb-6 text-center">
                A presença ativa da nossa liderança em eventos corporativos e reuniões estratégicas garante que a FF Travel esteja sempre atualizada com as melhores práticas do mercado internacional de turismo.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center p-6 bg-[#1e40af]/5 rounded-xl hover:bg-[#1e40af]/10 transition-colors">
                  <div className="w-14 h-14 bg-[#1e40af] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-medal-line text-2xl text-white"></i>
                  </div>
                  <p className="text-[#1e3a8a] font-bold mb-2">Credibilidade</p>
                  <p className="text-gray-600 text-sm">Reconhecidos no mercado</p>
                </div>

                <div className="text-center p-6 bg-[#1e40af]/5 rounded-xl hover:bg-[#1e40af]/10 transition-colors">
                  <div className="w-14 h-14 bg-[#1e40af] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-links-line text-2xl text-white"></i>
                  </div>
                  <p className="text-[#1e3a8a] font-bold mb-2">Conexões</p>
                  <p className="text-gray-600 text-sm">Rede global de parceiros</p>
                </div>

                <div className="text-center p-6 bg-[#1e40af]/5 rounded-xl hover:bg-[#1e40af]/10 transition-colors">
                  <div className="w-14 h-14 bg-[#ff6b35] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-star-line text-2xl text-white"></i>
                  </div>
                  <p className="text-[#1e3a8a] font-bold mb-2">Qualidade</p>
                  <p className="text-gray-600 text-sm">Serviços premium</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Imagem Institucional */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-[#ff6b35]/10 text-[#ff6b35] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Profissionalismo e Confiança
              </div>
              <h2 className="text-4xl font-bold text-[#1e3a8a] mb-6">
                FF Prestação &amp; Serviço, Lda
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                A FF Travel é uma empresa focada em soluções de viagem e serviços complementares, atuando com transparência, profissionalismo e atendimento direto ao cliente.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Oferecemos serviços completos de agência de viagens, incluindo passagens aéreas, pacotes turísticos internacionais, obtenção de vistos, reservas de hotéis e seguro de viagens para destinos internacionais.
              </p>
              <a
                href="https://wa.me/244936769113"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#ff6b35] text-white px-8 py-4 rounded-full font-bold hover:bg-[#ff5722] transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                Falar Connosco
              </a>
            </div>

            <div className="order-1 lg:order-2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/77a31689e51b90c373511edf5e93613a.jpeg"
                  alt="FF Travel - Profissionalismo e Confiança"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informações da Empresa */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl p-10 shadow-lg">
            <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6 text-center">
              Informações da Empresa
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1e40af]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-2xl text-[#1e40af]"></i>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Razão Social</p>
                  <p className="text-gray-800 font-semibold">FF Prestação &amp; Serviço, Lda</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1e40af]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-text-line text-2xl text-[#1e40af]"></i>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">NIF</p>
                  <p className="text-gray-800 font-semibold">500714988</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1e40af]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-[#1e40af]"></i>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Localização</p>
                  <p className="text-gray-800 font-semibold">Luanda, Angola</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1e40af]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-global-line text-2xl text-[#1e40af]"></i>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Área de Atuação</p>
                  <p className="text-gray-800 font-semibold">Angola e Países Lusófonos</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1e3a8a] mb-4">
              Nossos Valores
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-10 text-center shadow-lg">
              <div className="w-20 h-20 bg-[#1e40af]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-5xl text-[#1e40af]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">Transparência</h3>
              <p className="text-gray-600 leading-relaxed">
                Atuamos com clareza em todos os processos, sem taxas ocultas ou surpresas
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10 text-center shadow-lg">
              <div className="w-20 h-20 bg-[#1e40af]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-heart-line text-5xl text-[#1e40af]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">Profissionalismo</h3>
              <p className="text-gray-600 leading-relaxed">
                Equipa qualificada e comprometida em oferecer o melhor serviço
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10 text-center shadow-lg">
              <div className="w-20 h-20 bg-[#1e40af]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-2-line text-5xl text-[#1e40af]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">Atendimento Direto</h3>
              <p className="text-gray-600 leading-relaxed">
                Contacto direto com nossa equipa via WhatsApp para suporte rápido
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Serviços */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1e3a8a] mb-4">
              O Que Oferecemos
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-4">Agência de Viagens</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-[#ff6b35] flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Passagens aéreas e reservas</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-[#ff6b35] flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Pacotes turísticos internacionais</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-[#ff6b35] flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Processo de obtenção de vistos</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-[#ff6b35] flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Reservas de hotéis</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-[#ff6b35] flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Seguro de viagens</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-4">Loja &amp; Serviços</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-[#ff6b35] flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Venda de telemóveis e acessórios</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-[#ff6b35] flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Compra de ouro (alianças, anéis, fios, pulseiras)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-[#1e40af] to-[#1e3a8a]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Entre em Contacto
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Estamos prontos para ajudar com suas necessidades
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/244936769113"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#ff6b35] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-[#ff5722] transition-all shadow-2xl whitespace-nowrap cursor-pointer"
            >
              Falar no WhatsApp
            </a>
            <Link
              to="/contactos"
              className="inline-block bg-white/10 backdrop-blur-md text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer"
            >
              Ver Contactos
            </Link>
          </div>
        </div>
      </section>

      {/* Rodapé */}
      <footer className="bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <img
                src="https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/e349152b587c7809cba7381685cd68b4.png"
                alt="FF TRAVEL - Agência de Viagens"
                className="h-24 md:h-28 mb-8"
              />
              <p className="text-white/70 leading-relaxed mb-4">
                FF Prestação &amp; Serviço, Lda
              </p>
              <p className="text-white/50 text-sm mb-6">NIF: 500714988</p>

              {/* Redes Sociais - DESTAQUE */}
              <div className="mb-6">
                <p className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">
                  Siga-nos
                </p>
                <div className="flex gap-3">
                  <a
                    href="https://www.instagram.com/ff_travel_angola/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:scale-110 rounded-xl flex items-center justify-center transition-all shadow-lg cursor-pointer"
                    title="Instagram"
                  >
                    <i className="ri-instagram-line text-2xl text-white"></i>
                  </a>
                  <a
                    href="https://www.facebook.com/share/1AVqwyfH7E/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#1877F2] hover:scale-110 rounded-xl flex items-center justify-center transition-all shadow-lg cursor-pointer"
                    title="Facebook"
                  >
                    <i className="ri-facebook-fill text-2xl text-white"></i>
                  </a>
                  <a
                    href="https://www.linkedin.com/company/ff-travel"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#0A66C2] hover:scale-110 rounded-xl flex items-center justify-center transition-all shadow-lg cursor-pointer"
                    title="LinkedIn"
                  >
                    <i className="ri-linkedin-fill text-2xl text-white"></i>
                  </a>
                </div>
              </div>
            </div>

            <div>
              <p className="text-white/50 text-xs uppercase tracking-wider mb-4">
                Contactos
              </p>
              <div className="space-y-3">
                <a href="tel:+244936769113" className="block text-white hover:text-[#ff6b35] transition-colors cursor-pointer">
                  +244 936 769 113
                </a>
                <a href="tel:+244927606910" className="block text-white hover:text-[#ff6b35] transition-colors cursor-pointer">
                  +244 927 606 910
                </a>
              </div>
            </div>

            <div>
              <p className="text-white/50 text-xs uppercase tracking-wider mb-4">
                Localização
              </p>
              <p className="text-white/70 leading-relaxed">
                Rua do Campo do Escoqueiro<br />
                De frente ao prédio do Cucu<br />
                Luanda, Angola
              </p>
            </div>

            <div>
              <p className="text-white/50 text-xs uppercase tracking-wider mb-4">
                Links Rápidos
              </p>
              <div className="space-y-3">
                <Link to="/" className="block text-white hover:text-[#ff6b35] transition-colors">
                  Início
                </Link>
                <Link to="/agencia-viagens" className="block text-white hover:text-[#ff6b35] transition-colors">
                  Agência de Viagens
                </Link>
                <Link to="/loja-servicos" className="block text-white hover:text-[#ff6b35] transition-colors">
                  Loja &amp; Serviços
                </Link>
                <Link to="/contactos" className="block text-white hover:text-[#ff6b35] transition-colors">
                  Contactos
                </Link>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              © 2025 FF TRAVEL. Todos os direitos reservados.
            </p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white transition-colors cursor-pointer text-sm">
              Web Design
            </a>
          </div>
        </div>
      </footer>

      {/* Botão WhatsApp Flutuante */}
      <a
        href="https://wa.me/244936769113"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-[#ff6b35] rounded-full flex items-center justify-center text-white shadow-2xl hover:scale-110 transition-transform z-50 animate-pulse cursor-pointer"
        style={{ boxShadow: '0 8px 24px rgba(255, 107, 53, 0.4)' }}
      >
        <i className="ri-whatsapp-line text-3xl"></i>
      </a>
    </>
  );
}
